import { useState } from 'react';
import { Screen } from '../../app/Frame';
import { useWritable } from '../../app/MeContext';
import { useScreen, useSheet, useToast } from '../../app/shell-context';
import { ApiError } from '../../api/client';
import { normalizeTg, TG_RE } from '../../lib/org';
import { pluralWithCount } from '../../lib/plural';
import { showConfirm } from '../../lib/telegram';
import { Notice, Pill } from '../../ui/parts';
import { FormSkeleton, LoadFailed, Spinner } from '../org-parts';
import { useAdminList, useAdminMutations } from '../api';
import type { AdminRecord } from '../types';

/**
 * Подэкран «Администраторы». Отдельно от специалистов не для порядка: список
 * специалистов кормит запись, загрузку дня и отчёты по выплатам, и администратор
 * без своих услуг даёт там лишнего человека в выборе «к кому записаться».
 *
 * Правится только своя строка: имя человека меняет человек. Чужую можно либо
 * оставить, либо снять права целиком — середины здесь нет.
 */
export function OrgAdmins() {
  const writable = useWritable();
  const sheet = useSheet();
  const toast = useToast();
  const admins = useAdminList();
  const { remove, leave } = useAdminMutations();

  useScreen('Администраторы', null);

  if (admins.isError) {
    return <Screen><LoadFailed busy={admins.isFetching} onRetry={() => void admins.refetch()} /></Screen>;
  }
  if (admins.isPending) {
    return <Screen><FormSkeleton rows={3} /></Screen>;
  }

  const list = admins.data ?? [];
  const me = list.find((a) => a.isMe) ?? null;
  const alone = list.length <= 1;
  const busy = remove.isPending || leave.isPending;

  async function demote(a: AdminRecord) {
    const ok = await showConfirm(
      `Снять права администратора с «${a.display_name}»? Записи и карточка специалиста останутся.`,
      { title: 'Снять права', destructive: true },
    );
    if (!ok) return;
    remove.mutate(a.tg_id, {
      onSuccess: () => toast('Права сняты'),
      onError: (e) => toast(e instanceof ApiError ? e.message : 'Не получилось снять права'),
    });
  }

  async function handover() {
    const ok = await showConfirm(
      'Вы перестанете быть администратором и вернётесь к обычным экранам. Вернуть права сможет только другой администратор.',
      { title: 'Передать управление', destructive: true },
    );
    if (!ok) return;
    leave.mutate(undefined, {
      // Роль меняется на сервере, поэтому перезапрашиваем не список, а всё
      // приложение: после выхода админка этому человеку больше не принадлежит.
      onSuccess: () => window.location.assign('/'),
      onError: (e) => toast(e instanceof ApiError ? e.message : 'Не получилось выйти'),
    });
  }

  return (
    <Screen>
      <p className="lead">
        Администратор управляет организацией: настройками, услугами, специалистами и клиентами.
        Сам он клиентов не ведёт, поэтому в списке специалистов его нет.
      </p>

      <div className="rows">
        {list.map((a) => (
          <button
            className="rw"
            key={a.tg_id}
            disabled={!writable || (!a.isMe && busy)}
            onClick={() => (a.isMe ? sheet.open(<EditMeSheet admin={a} />) : void demote(a))}
          >
            <span className="rw-body">
              <b>{a.display_name || 'Без имени'}</b>
              <span>
                {a.tg_username ? `@${a.tg_username}` : 'ник не указан'}
                {a.specialist_id ? ' · также специалист' : ''}
              </span>
            </span>
            {a.isMe ? <Pill kind="paid">вы</Pill> : <span className="linkish danger">снять права</span>}
          </button>
        ))}
      </div>

      {writable ? (
        <button className="add" onClick={() => sheet.open(<InviteAdminSheet />)}>＋ Пригласить администратора</button>
      ) : null}

      {alone ? null : (
        <p className="lead">
          Сейчас {pluralWithCount(list.length, 'администратор', 'администратора', 'администраторов')}.
        </p>
      )}

      {writable && me ? (
        <div className="danger-zone">
          <button className="linkish danger" type="button" disabled={alone || busy} onClick={() => void handover()}>
            {leave.isPending ? 'Выходим…' : 'Передать управление и выйти'}
          </button>
          {alone ? <span className="hint">Сначала пригласите второго администратора</span> : null}
        </div>
      ) : null}
    </Screen>
  );
}

/** Своя карточка: имя и ник. Ник уходит клиентам в сообщениях об отменах. */
function EditMeSheet({ admin }: { admin: AdminRecord }) {
  const sheet = useSheet();
  const toast = useToast();
  const { updateMe } = useAdminMutations();
  const [name, setName] = useState(admin.display_name);
  const [tg, setTg] = useState(admin.tg_username ? `@${admin.tg_username}` : '');

  const nameOk = name.trim().length >= 2 && name.trim().length <= 120;
  const tgOk = !tg.trim() || TG_RE.test(normalizeTg(tg));
  const changed = name.trim() !== admin.display_name || normalizeTg(tg).replace(/^@/, '') !== admin.tg_username;

  return (
    <>
      <h3>Ваша карточка</h3>
      <div className="field">
        <label htmlFor="adName">Имя</label>
        <input id="adName" type="text" value={name} onChange={(e) => setName(e.target.value)} />
        {admin.specialist_id ? (
          <span className="hint">Это же имя стоит в вашей карточке специалиста — поменяем в обеих.</span>
        ) : null}
      </div>
      <div className="field">
        <label htmlFor="adTg">Ник в Telegram</label>
        <input
          id="adTg" type="text" placeholder="@username" autoCapitalize="off" spellCheck={false}
          value={tg} onChange={(e) => setTg(e.target.value)} onBlur={() => setTg(normalizeTg(tg))}
        />
        <span className={tgOk ? 'hint' : 'err'}>
          {tgOk ? 'По нему с вами свяжутся коллеги' : 'Латиница, цифры и _, от 5 до 32 символов'}
        </span>
      </div>

      <div className="row-actions">
        <button className="btn" onClick={() => sheet.close()}>Отмена</button>
        <button
          className="btn solid"
          disabled={!nameOk || !tgOk || !changed || updateMe.isPending}
          onClick={() =>
            updateMe.mutate(
              { displayName: name.trim(), tgUsername: normalizeTg(tg).replace(/^@/, '') },
              {
                onSuccess: () => {
                  toast('Сохранено');
                  sheet.close();
                },
                onError: (e) => toast(e instanceof ApiError ? e.message : 'Не получилось сохранить'),
              },
            )
          }
        >
          Сохранить
        </button>
      </div>
    </>
  );
}

/**
 * Приглашение. Бот не может написать первым тому, кто ему не писал, поэтому
 * ссылку администратор передаёт сам — как угодно, лишь бы дошла.
 */
function InviteAdminSheet() {
  const sheet = useSheet();
  const toast = useToast();
  const { invite } = useAdminMutations();
  const [name, setName] = useState('');
  const [link, setLink] = useState<string | null>(null);
  const [noBot, setNoBot] = useState(false);

  if (link) {
    return (
      <>
        <h3>Ссылка готова</h3>
        <div className="field">
          <label htmlFor="invLink">Ссылка-приглашение</label>
          <input id="invLink" type="text" readOnly value={link} onFocus={(e) => e.currentTarget.select()} />
          <span className="hint">
            Одноразовая, 14 дней. По ней человек попадёт сразу в админку этой организации.
          </span>
        </div>
        <button
          className="btn"
          onClick={() => {
            void navigator.clipboard?.writeText(link);
            toast('Ссылка скопирована');
          }}
        >
          Скопировать ссылку
        </button>
        <button className="btn solid" onClick={() => sheet.close()}>Готово</button>
      </>
    );
  }

  return (
    <>
      <h3>Пригласить администратора</h3>
      <p>
        У человека будут те же права, что у вас: настройки, услуги, специалисты и клиенты.
        Карточку специалиста ссылка не заводит — клиентов он вести не начнёт.
      </p>

      <div className="field">
        <label htmlFor="invName">Как его назвать</label>
        <input
          id="invName" type="text" placeholder="Мария Соколова"
          value={name} onChange={(e) => setName(e.target.value)}
        />
        <span className="hint">Необязательно: имя из Telegram он подтвердит сам при входе.</span>
      </div>

      {noBot ? (
        <Notice>
          Ссылку собрать не из чего: у организации не настроен бот. Напишите владельцу сервиса —
          без бота приглашению некуда вести.
        </Notice>
      ) : null}

      <div className="row-actions">
        <button className="btn" onClick={() => sheet.close()}>Отмена</button>
        <button
          className="btn solid"
          disabled={invite.isPending}
          onClick={() =>
            invite.mutate(
              { displayName: name.trim() || undefined },
              {
                onSuccess: (res) => {
                  if (res.link) setLink(res.link);
                  else setNoBot(true);
                },
                onError: (e) => toast(e instanceof ApiError ? e.message : 'Не получилось создать ссылку'),
              },
            )
          }
        >
          {invite.isPending ? <><Spinner /> Создаём…</> : 'Создать ссылку'}
        </button>
      </div>
    </>
  );
}
